import React from "react"

import { useEvent } from "shared/lib/useEvent"
import { ProductInformation } from "shared/types/__generated__/graphql"

import { useProductInformationClipboardShare } from "entities/products/model/useProductInformationClipboardShare"

import {
  ProductTenderTextContext,
  ProductTenderTextContextValue
} from "./ProductTenderTextContext"
import "./styles/_product-tender-text.scss"
import { ProductTenderTextCopyToClipboard } from "./ui/ProductTenderTextCopyToClipboard"
import { ProductTenderTextGeneralDescription } from "./ui/ProductTenderTextGeneralDescription"
import { ProductTenderTextVariantDescription } from "./ui/ProductTenderTextVariantDescription"

interface ProductTenderTextRootProps {
  productInformation: ProductInformation | null | undefined
  isLoading?: boolean
  children?: React.ReactNode
}

const ProductTenderTextRoot = React.memo(
  ({
    productInformation,
    isLoading = false,
    children
  }: ProductTenderTextRootProps) => {
    const copyToClipboardFn =
      useProductInformationClipboardShare(productInformation)
    const copyToClipboard = useEvent(() => copyToClipboardFn())

    const hasGeneralDescription =
      !!productInformation?.generalDescription || isLoading
    const hasVariantDescription =
      !!productInformation?.variantDescription || isLoading

    const contextValue = React.useMemo<ProductTenderTextContextValue>(
      () => ({ productInformation, isLoading, copyToClipboard }),
      // copyToClipboard is stable via useEvent
      [productInformation, isLoading]
    )

    if (!hasGeneralDescription && !hasVariantDescription) return null

    return (
      <ProductTenderTextContext.Provider value={contextValue}>
        {children}
      </ProductTenderTextContext.Provider>
    )
  }
)

export const ProductTenderText = Object.assign(ProductTenderTextRoot, {
  Root: ProductTenderTextRoot,
  CopyToClipboard: ProductTenderTextCopyToClipboard,
  GeneralDescription: ProductTenderTextGeneralDescription,
  VariantDescription: ProductTenderTextVariantDescription
})
